import React, { useMemo } from 'react';
import { Settings } from 'lucide-react';
import { useSceneStore } from '../../store/sceneStore';
import { usePanelStore } from '../../store/panelStore';
import { Panel } from '../panels/Panel';
import { GameObjectSection } from './GameObjectSection';
import { TransformSection } from './TransformSection';
import { ComponentSection } from './ComponentSection';
import { AddComponentDropdown } from './AddComponentDropdown';
import { cn } from '../../utils/cn';

export const InspectorPanel: React.FC = () => {
  const { selectedIds, gameObjects } = useSceneStore();
  const { panels } = usePanelStore();

  const select = useSceneStore((s) => s.select);
  const panelConfig = panels['inspector'];

  // Get the first selected game object (for now, only single selection editing)
  const selectedGameObject = useMemo(() => {
    if (selectedIds.length === 0) return null;
    return gameObjects[selectedIds[0]] || null;
  }, [selectedIds, gameObjects]);

  const multipleSelected = selectedIds.length > 1;

  // Check if selected object is a sub-child of a cascade-slot-part container
  const slotPartParent = useMemo(() => {
    if (!selectedGameObject?.parentId) return null;
    const parent = gameObjects[selectedGameObject.parentId];
    if (!parent) return null;
    const hasPartComponent = parent.components.some((c) => c.type === 'cascade-slot-part');
    if (!hasPartComponent) return null;
    return parent;
  }, [selectedGameObject, gameObjects]);

  return (
    <Panel panel={panelConfig} className="h-full">
      <div className="flex flex-col h-full overflow-hidden">
        {/* No selection state */}
        {!selectedGameObject && (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
              <Settings size={24} className="text-zinc-600" />
            </div>
            <h3 className="text-sm font-medium text-zinc-400 mb-1">
              No object selected
            </h3>
            <p className="text-xs text-zinc-600 max-w-[200px]">
              Select an object in the Hierarchy or Scene View to inspect its properties
            </p>
          </div>
        )}

        {/* Multiple selection state */}
        {selectedGameObject && multipleSelected && (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
              <Settings size={24} className="text-zinc-600" />
            </div>
            <h3 className="text-sm font-medium text-zinc-400 mb-1">
              Multiple objects selected
            </h3>
            <p className="text-xs text-zinc-600 max-w-[200px]">
              {selectedIds.length} objects selected. Multi-edit is not yet supported.
            </p>
          </div>
        )}

        {/* Single selection - show inspector */}
        {selectedGameObject && !multipleSelected && (
          <div className="flex-1 overflow-y-auto">
            {/* Slot part parent context banner */}
            {slotPartParent && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800/60 border-b border-zinc-700 text-xs text-zinc-400">
                <span>
                  Part of: <span className="text-zinc-300 font-medium">{slotPartParent.name}</span> (Slot Part)
                </span>
                <button
                  onClick={() => select(slotPartParent.id)}
                  className="ml-auto text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Select Parent
                </button>
              </div>
            )}

            {/* GameObject Section */}
            <GameObjectSection gameObject={selectedGameObject} />

            {/* Transform Section */}
            <TransformSection gameObject={selectedGameObject} />

            {/* Component Sections */}
            {selectedGameObject.components.map((component) => (
              <ComponentSection
                key={component.id}
                objectId={selectedGameObject.id}
                component={component}
              />
            ))}

            {/* Add Component Button */}
            <AddComponentDropdown objectId={selectedGameObject.id} />

            {/* Bottom padding for scroll */}
            <div className="h-4" />
          </div>
        )}
      </div>
    </Panel>
  );
};
